/**
 * Who is on an item, at the top of its page (and of its quick look), as documents apps show it: yourself last (so it's
 * there to be found even alone), the others before you, a picture per
 * person (or agent) however many tabs and devices they have it open in, each ringed in a colour of their own, dimmed
 * when they're idle or looking elsewhere, a hint when typing (or, an agent, working); past a few, "+N", which also
 * counts who's looking by its public link (never named). Invited guests (shared with them) are people like the
 * others, marked "Guest" in the list; they see the stack too, on their page of the item. It opens a list of everyone,
 * with what they're doing.
 */
import { useEffect, useRef, useState } from "preact/hooks";
import { t } from "../i18n/index.ts";
import { displayName } from "../lib/people.ts";
import { type Person, usePresence } from "../lib/presence.ts";
import { labelOf, ringFor, statusOf } from "../lib/presenceLook.ts";
import { me } from "../lib/state.ts";
import { useId } from "../lib/uid.ts";
import { Avatar } from "./Avatar.tsx";
import { placePopover } from "./popover.ts";

/** Pictures shown before "+N". */
const MAX_SHOWN = 3;
/** How long someone who left takes to fade out. */
const LEAVE_MS = 200;

/** The people shown, those who just left kept a moment (fading out) unless motion is reduced. */
function useLeaving(people: readonly Person[]): (Person & { leaving: boolean })[] {
  // Who left, and where they were in the stack (they fade out in their place).
  const [gone, setGone] = useState<readonly { p: Person; at: number }[]>([]);
  const last = useRef<readonly Person[]>([]);
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);
  useEffect(() => () => timers.current.forEach(clearTimeout), []);
  useEffect(() => {
    const now = new Set(people.map((p) => p.user));
    const left = last.current.flatMap((p, at) => (now.has(p.user) ? [] : [{ p, at }]));
    last.current = people;
    if (left.length === 0 || globalThis.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      return;
    }
    setGone((g) => [...g.filter((x) => !now.has(x.p.user)), ...left]);
    timers.current.push(setTimeout(() => setGone((g) => g.filter((x) => !left.includes(x))), LEAVE_MS));
  }, [people]);
  const out: (Person & { leaving: boolean })[] = people.map((p) => ({ ...p, leaving: false }));
  const here = new Set(people.map((p) => p.user));
  for (const { p, at } of gone) {
    if (!here.has(p.user)) {
      out.splice(Math.min(at, out.length), 0, { ...p, leaving: true });
    }
  }
  return out;
}

/** One person's picture in the stack. */
function Face({ p, size }: { p: Person & { leaving: boolean }; size: number }) {
  const working = p.kind === "agent" && p.status !== "idle";
  return (
    <span
      title={labelOf(p)}
      data-user={p.user}
      data-status={p.status}
      class={`relative inline-flex shrink-0 rounded-full ring-2 ring-offset-2 ring-offset-white ${ringFor(p.colour, p.user)} transition-[opacity,scale] duration-200 ease-out starting:scale-75 starting:opacity-0 motion-reduce:transition-none ${p.leaving ? "scale-75 opacity-0" : p.active ? "" : "opacity-50"}`}
    >
      <Avatar
        id={p.user}
        name={displayName(p.user, p.name)}
        kind={p.kind === "agent" ? "agent" : "human"}
        size={size}
      />
      {p.status === "typing" ? (
        // Typing: three dots by their picture.
        <span
          aria-hidden="true"
          class="absolute -right-1.5 -bottom-1 flex items-center gap-px rounded-full bg-white px-1 py-0.5 shadow-sm ring-1 ring-zinc-950/10"
        >
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              class="size-1 animate-pulse rounded-full bg-zinc-500 motion-reduce:animate-none"
              style={{ animationDelay: `${i * 150}ms` }}
            />
          ))}
        </span>
      ) : working ? (
        // An agent at work: a beat by its picture.
        <span
          aria-hidden="true"
          class="absolute -right-0.5 -bottom-0.5 size-2.5 animate-pulse rounded-full bg-green-500 ring-2 ring-white motion-reduce:animate-none"
        />
      ) : null}
    </span>
  );
}

/** Everyone, listed (the stack's popover). */
function List({ people, guests, self }: { people: readonly Person[]; guests: number; self: Person | null }) {
  return (
    <>
      <p class="text-xs font-medium text-zinc-500">{t("presence.title")}</p>
      <ul class="flex flex-col gap-2">
        {self ? (
          <li class="flex items-center gap-2">
            <span class="inline-flex rounded-full ring-1 ring-zinc-950/10">
              <Avatar id={self.user} name={self.name} size={24} />
            </span>
            <span class="min-w-0 flex-1 truncate font-medium text-zinc-900">{t("presence.you")}</span>
          </li>
        ) : null}
        {people.map((p) => (
          <li key={p.user} class={`flex items-center gap-2 ${p.active ? "" : "text-zinc-500"}`}>
            <span class={`inline-flex rounded-full ring-2 ${ringFor(p.colour, p.user)}`}>
              <Avatar
                id={p.user}
                name={displayName(p.user, p.name)}
                kind={p.kind === "agent" ? "agent" : "human"}
                size={24}
              />
            </span>
            <span class="min-w-0 flex-1">
              <span class="flex items-center gap-1.5">
                <span class="truncate font-medium text-zinc-900">{displayName(p.user, p.name)}</span>
                {p.guest ? (
                  <span class="shrink-0 rounded bg-zinc-100 px-1 text-xs text-zinc-600 ring-1 ring-zinc-950/10">
                    {t("presence.guest")}
                  </span>
                ) : null}
              </span>
              <span class="block truncate text-xs">{statusOf(p)}</span>
            </span>
          </li>
        ))}
      </ul>
      {guests > 0 ? <p class="text-xs text-zinc-500">{t("presence.guests", { count: guests })}</p> : null}
    </>
  );
}

/** You, as the stack shows you: here, looking at it. */
function selfOf(view: string): Person | null {
  const m = me.value;
  return m
    ? {
        user: m.id,
        name: m.name,
        kind: "human",
        guest: false,
        status: "viewing",
        text: null,
        // Shown uncoloured.
        colour: "",
        active: true,
        views: [view],
        states: [],
        since: "",
      }
    : null;
}

/**
 * Who is in `room` (`item:<id>`, or an invited guest's `shared:<id>`): the others, then you (there as `view`: `page`, `peek`), so it shows even while you're
 * alone.
 */
export function PresenceStack({ room, view = "page", size = 28 }: { room: string; view?: string; size?: number }) {
  const { people, guests } = usePresence(room, view);
  const faces = useLeaving(people);
  const id = useId();
  const pop = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const self = selfOf(view);
  const total = people.length + guests;
  if (faces.length === 0 && guests === 0 && !self) {
    return null;
  }
  const shown = faces.slice(0, MAX_SHOWN);
  // Those not shown, and the guests, as one number.
  const more = Math.max(0, people.length - shown.filter((p) => !p.leaving).length) + guests;
  return (
    <>
      <button
        ref={trigger}
        type="button"
        popovertarget={id}
        aria-label={total > 0 ? t("presence.here", { count: total }) : t("presence.onlyYou")}
        class="flex shrink-0 items-center gap-2 rounded-full p-1 hover:bg-zinc-100 focus-visible:outline-2 focus-visible:outline-blue-600"
      >
        {shown.map((p) => (
          <Face key={p.user} p={p} size={size} />
        ))}
        {self ? (
          // You: no colour of your own (it tells the others apart, not you from yourself).
          <span title={t("presence.you")} class="inline-flex shrink-0 rounded-full ring-1 ring-zinc-950/10">
            <Avatar id={self.user} name={self.name} size={size} />
          </span>
        ) : null}
        {more > 0 ? (
          <span
            class="inline-flex shrink-0 items-center justify-center rounded-full bg-zinc-100 px-1.5 text-xs font-medium text-zinc-700 tabular-nums ring-1 ring-zinc-950/10"
            style={{ height: `${size}px`, minWidth: `${size}px` }}
          >
            {`+${more}`}
          </span>
        ) : null}
      </button>
      <div
        ref={pop}
        id={id}
        popover="auto"
        role="dialog"
        aria-label={t("presence.title")}
        class="fixed m-0 w-72 max-w-[calc(100vw-1rem)] flex-col gap-3 rounded-lg border border-zinc-200 bg-white p-4 text-sm shadow-lg [&:popover-open]:flex"
        onToggle={(e) => {
          if (e.newState === "open" && pop.current && trigger.current) {
            placePopover(pop.current, trigger.current);
          }
        }}
      >
        <List people={people} guests={guests} self={self} />
      </div>
    </>
  );
}
